import * as PIXI from 'pixi.js';

import { TOP_LAYER_ZINDEX } from '../../constants/Layers';
import Color from '../Color';
import {
  getXAccountingForAnchor,
  getYAccountingForAnchor,
} from '../CoordinateCalculator';
import { horizontalAlignGuides, verticalAlignGuides } from './alignmentAxes';

export default class AlignmentGuides {
  static _instance = null;

  static getInstance(app) {
    if (AlignmentGuides._instance === null) {
      AlignmentGuides._instance = new AlignmentGuides(app);
    }
    return this._instance;
  }

  static resetInstance(app) {
    AlignmentGuides._instance = new AlignmentGuides(app);
  }

  constructor(app) {
    this.app = app;

    this.color = Color.toNumber(Color.RED);
    this.displayAllowance = 10;
    this.snapAllowance = 10;

    // prepare the container that will hold our graphic overlays
    this.container = new PIXI.Container();
    this.container.interactive = true;
    this.container.width = this.app.screen.width;
    this.container.height = this.app.screen.height;
    this.container.zIndex = TOP_LAYER_ZINDEX;

    this.horizontalAlignGuides = horizontalAlignGuides(this.app.screen);
    this.verticalAlignGuides = verticalAlignGuides(this.app.screen);

    this.getAllAlignGuides().forEach((alignGuide) => {
      alignGuide.graphics = new PIXI.Graphics();
      this.container.addChild(alignGuide.graphics);
    });

    this.render();
    this.hideAll();
  }

  getAllAlignGuides() {
    return [...this.horizontalAlignGuides, ...this.verticalAlignGuides];
  }

  show(graphics) {
    graphics.visible = true;
  }

  hide(graphics) {
    graphics.visible = false;
  }

  hideAll() {
    this.getAllAlignGuides().forEach((alignGuide) =>
      this.hide(alignGuide.graphics)
    );
  }

  toggleShowHideFor = ({ x, y, width, height }, isScaling) => {
    this.horizontalAlignGuides.forEach((alignGuide) => {
      this.isWithinAllowanceOf(this.displayAllowance, y, alignGuide.y0) || // Top
      this.isWithinAllowanceOf(
        this.displayAllowance,
        y + height,
        alignGuide.y0
      ) || // Bottom
      (!isScaling &&
        this.isWithinAllowanceOf(
          this.displayAllowance,
          y + height / 2,
          alignGuide.y0
        )) // Y Center
        ? this.show(alignGuide.graphics)
        : this.hide(alignGuide.graphics);
    });

    this.verticalAlignGuides.forEach((alignGuide) => {
      this.isWithinAllowanceOf(this.displayAllowance, x, alignGuide.x0) || // Left
      this.isWithinAllowanceOf(
        this.displayAllowance,
        x + width,
        alignGuide.x0
      ) || // Right
      (!isScaling &&
        this.isWithinAllowanceOf(
          this.displayAllowance,
          x + width / 2,
          alignGuide.x0
        )) // X Center
        ? this.show(alignGuide.graphics)
        : this.hide(alignGuide.graphics);
    });
  };

  snapToGuidesByScale = (contents, anchor) => {
    let snappedScale = -1;

    const { x, y, width, height } = contents.getBounds();
    const unscaledWidth = width / contents.scale.x;
    const unscaledHeight = height / contents.scale.y;

    this.verticalAlignGuides.forEach((alignGuide) => {
      // align left bound of draggable
      if (
        this.isWithinAllowanceOf(this.snapAllowance, x, alignGuide.x0) &&
        unscaledWidth !== 0
      ) {
        const snapDistance = x - alignGuide.x0;
        const newWidth = width + snapDistance / (1 - anchor.x);
        snappedScale = Math.max(snappedScale, newWidth / unscaledWidth);
      }
      // align right bound of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          x + width,
          alignGuide.x0
        ) &&
        unscaledWidth !== 0
      ) {
        const snapDistance = alignGuide.x0 - width - x;
        const newWidth = width + snapDistance / (1 - anchor.x);
        snappedScale = Math.max(snappedScale, newWidth / unscaledWidth);
      }
    });

    this.horizontalAlignGuides.forEach((alignGuide) => {
      // align top bound of draggable
      if (
        this.isWithinAllowanceOf(this.snapAllowance, y, alignGuide.y0) &&
        unscaledHeight !== 0
      ) {
        const snapDistance = y - alignGuide.y0;
        const newHeight = height + snapDistance / (1 - anchor.y);
        snappedScale = Math.max(snappedScale, newHeight / unscaledHeight);
      }
      // align bottom bound of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          y + height,
          alignGuide.y0
        ) &&
        unscaledHeight !== 0
      ) {
        const snapDistance = alignGuide.y0 - height - y;
        const newHeight = height + snapDistance / (1 - anchor.y);
        snappedScale = Math.max(snappedScale, newHeight / unscaledHeight);
      }
    });

    return snappedScale === -1 ? contents.scale.x : snappedScale;
  };

  snapToGuidesByPosition = ({ x, y, width, height }, anchor) => {
    const originX = getXAccountingForAnchor({
      x,
      width,
      anchor,
    });
    const originY = getYAccountingForAnchor({
      y,
      height,
      anchor,
    });
    const anchorOffsetX = anchor.x * width;
    const anchorOffsetY = anchor.y * height;

    let snappedX = x; // default to current center of draggable
    let snappedY = y; // default to current center of draggable

    this.verticalAlignGuides.forEach((alignGuide) => {
      // align left bound of draggable
      if (
        this.isWithinAllowanceOf(this.snapAllowance, originX, alignGuide.x0)
      ) {
        snappedX = alignGuide.x0 + anchorOffsetX;
      }
      // align right bound of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          originX + width,
          alignGuide.x0
        )
      ) {
        snappedX = alignGuide.x0 - width + anchorOffsetX;
      }
      // align center of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          originX + width / 2,
          alignGuide.x0
        )
      ) {
        snappedX = alignGuide.x0 - width / 2 + anchorOffsetX;
      }
    });

    this.horizontalAlignGuides.forEach((alignGuide) => {
      // align top bound of draggable
      if (
        this.isWithinAllowanceOf(this.snapAllowance, originY, alignGuide.y0)
      ) {
        snappedY = alignGuide.y0 + anchorOffsetY;
      }
      // align bottom bound of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          originY + height,
          alignGuide.y0
        )
      ) {
        snappedY = alignGuide.y0 - height + anchorOffsetY;
      }
      // align center of draggable
      if (
        this.isWithinAllowanceOf(
          this.snapAllowance,
          originY + height / 2,
          alignGuide.y0
        )
      ) {
        snappedY = alignGuide.y0 - height / 2 + anchorOffsetY;
      }
    });

    return { snappedX, snappedY };
  };

  isWithinAllowanceOf = (allowance, value, axis) => {
    return Math.abs(value - axis) <= allowance;
  };

  render() {
    this.getAllAlignGuides().forEach((alignGuide) => {
      alignGuide.graphics
        .clear()
        .lineStyle(alignGuide.weight, this.color)
        .moveTo(alignGuide.x0, alignGuide.y0)
        .lineTo(alignGuide.x1, alignGuide.y1);
    });
  }
}
